import get from "lodash/get";

import { ENV, ENVS } from "cortex/ui/src/constant/app";
import { User } from "cortex/ui/src/user/model";

import type { Scope, SentryEvent, User as SentryUser } from "@sentry/browser";
import * as Sentry from "@sentry/browser";

type EnvironmentName = (typeof ENVS)[keyof typeof ENVS];

const SENTRY_ENABLED_ENVS = new Set<EnvironmentName>([
  ENVS.PRODUCTION,
  ENVS.STAGING,
  ENVS.DEVELOPMENT,
]);

export function init(): void {
  const deployTarget = ENV.DEPLOY_TARGET as EnvironmentName;

  if (SENTRY_ENABLED_ENVS.has(deployTarget)) {
    Sentry.init({
      beforeSend: dropEvents,
      dsn: ENV.SENTRY_DSN,
      environment: ENV.DEPLOY_TARGET,
      release: `ui@${ENV.COMMIT_VERSION}`,
    });

    Sentry.configureScope((scope: Scope) => {
      scope.setTag("app", ENV.APP_NAME);
    });
  }
}

function dropEvents(event: SentryEvent): SentryEvent | null {
  const statusCode = get(event, "extra.Error.status") as number | undefined;
  if (statusCode !== undefined && [401, 403].includes(statusCode)) {
    return null;
  }

  return event;
}

export function setUser(user: User | null | undefined): void {
  Sentry.configureScope((scope: Scope) => {
    if (user) {
      const primaryAccount = user.account?.[0];
      const sentryUser: SentryUser = {
        id: String(user.id),
        email: user.email,
      };

      scope.setUser(sentryUser);
      scope.setTag(
        "accountId",
        user.account_id !== undefined ? String(user.account_id) : "",
      );
      scope.setTag("accountName", primaryAccount?.name ?? "");
    } else {
      scope.setUser({} as SentryUser);
      scope.setTag("accountId", "");
      scope.setTag("accountName", "");
    }
  });
}

/*
 * For manual messages, set the message context to include the stack
 * trace.
 */
export function captureStack(): void {
  let stack: Error | undefined;

  try {
    throw new Error("Capture Stack");
  } catch (error) {
    if (error instanceof Error) {
      stack = error;
    }
  }

  Sentry.configureScope((scope: Scope) => {
    scope.setExtra("stack", stack);
  });
}
